import React, { useState, useEffect, useContext, useMemo } from "react";
import styled from "styled-components";
import {
  Panel,
  FlexboxGrid,
  Message,
  IconButton,
  Divider,
  Button,
} from "rsuite";
import PlusIcon from "@rsuite/icons/Plus";
import ReloadIcon from "@rsuite/icons/Reload";
import { DarkModeContext } from "../../../../../appContexts/DarkModeContext";
import { getDataMappingConfig } from "../charts/_helpers/getDataMappingConfig";
import { useChartData } from "./hooks/useChartData";
import { useChartConfig } from "./hooks/useChartConfig";
import { useUnitConversion } from "./hooks/useUnitConversion";
import DataPointSelector from "./DataPointSelector";
import ChartRenderer from "./ChartRenderer";
import ChartControls from "./ChartControls";
import FullscreenModal from "./FullscreenModal";
import UnitToggle from "./UnitToggle";
import { colors, GlassCard } from "../../../../ui";

const UnifiedChart = ({
  rawData,
  dataConfig,
  metadata,
  alertConfigs,
  className,
  ...props
}) => {
  const { darkMode } = useContext(DarkModeContext);
  const [selectedMetrics, setSelectedMetrics] = useState([]);
  const [timeRange, setTimeRange] = useState("24h");
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [chartLibrary, setChartLibrary] = useState("canvas"); // 'canvas' or 'fusion'
  const [showSelector, setShowSelector] = useState(true);

  // Get available metrics from actual data instead of hardcoded manufacturer config
  const availableMetrics = useMemo(() => {
    if (!rawData?.filterConfig) return [];

    // Convert filterConfig to the format expected by DataPointSelector
    return rawData.filterConfig
      .map(([fieldName, label, level, type]) => {
        // Determine if field is continuous or discrete based on name patterns
        const isDiscrete =
          fieldName.includes("status") ||
          fieldName.includes("alarm") ||
          fieldName.includes("switch") ||
          fieldName.includes("error");

        return [
          [fieldName], // field as array (for consistency with getDataMappingConfig format)
          isDiscrete ? "discrete" : "continuous", // type
          level <= 2, // enabled (show level 1 and 2 fields)
        ];
      })
      .filter(([fields, type, enabled]) => enabled);
  }, [rawData?.filterConfig]);

  // Default field group function
  const loadDefaultFieldGroup = () => {
    if (availableMetrics.length === 0) return [];

    const allMetrics = availableMetrics.map(([fields]) => fields.join("_"));

    const defaultGroup = [];

    // 1. Pressure (any pressure field)
    const pressureMetric = allMetrics.find(
      (metric) =>
        metric.toLowerCase().includes("pressure") ||
        metric.toLowerCase().includes("psia") ||
        metric.toLowerCase().includes("mag_psia"),
    );
    if (pressureMetric) defaultGroup.push(pressureMetric);

    // 2. Compressor Vib Status
    const compVibMetric = allMetrics.find(
      (metric) =>
        metric.toLowerCase().includes("comp_vib") ||
        (metric.toLowerCase().includes("compressor") &&
          metric.toLowerCase().includes("status")),
    );
    if (compVibMetric) defaultGroup.push(compVibMetric);

    /* 3. Probe 1 (temp_probe_0 or similar)
    const probe1Metric = allMetrics.find(
      (metric) =>
        metric.toLowerCase().includes("temp_probe_0") ||
        metric.toLowerCase().includes("probe_0") ||
        metric.toLowerCase().includes("probe1") ||
        metric.toLowerCase().includes("probe_1"),
    );
    if (probe1Metric) defaultGroup.push(probe1Metric);

    // 4. Probe 2 (temp_probe_1 or similar)
    const probe2Metric = allMetrics.find(
      (metric) =>
        metric.toLowerCase().includes("temp_probe_1") ||
        metric.toLowerCase().includes("probe_1") ||
        metric.toLowerCase().includes("probe2") ||
        metric.toLowerCase().includes("probe_2"),
    );
    if (probe2Metric) defaultGroup.push(probe2Metric);*/

    // 5. HE Level (he_level_value  )
    const heLevelMetric = allMetrics.find(
      (metric) =>
        metric.toLowerCase().includes("he_level_value") ||
        metric.toLowerCase().includes("he_level") ||
        metric.toLowerCase().includes("he_level_value_") ||
        metric.toLowerCase().includes("he_level_"),
    );
    if (heLevelMetric) defaultGroup.push(heLevelMetric);

    return defaultGroup;
  };

  // Set default selection on component mount
  useEffect(() => {
    if (availableMetrics.length > 0 && selectedMetrics.length === 0) {
      const defaultMetrics = loadDefaultFieldGroup();

      // If default group has items, use it; otherwise fall back to original logic
      if (defaultMetrics.length > 0) {
        setSelectedMetrics(defaultMetrics);
      } else {
        // Fallback: Auto-select metrics with HE Level always included
        const continuousMetrics = availableMetrics
          .filter(([fields, type, enabled]) => enabled && type === "continuous")
          .map(([fields]) => fields.join("_"));

        const prioritizedMetrics = [];
        const heLevelMetric = continuousMetrics.find(
          (metric) =>
            metric.toLowerCase().includes("helevel") ||
            metric.toLowerCase().includes("he_level") ||
            metric.toLowerCase().includes("lhepct"),
        );

        if (heLevelMetric) prioritizedMetrics.push(heLevelMetric);

        const hePressureMetric = continuousMetrics.find(
          (metric) =>
            (metric.includes("he_pressure") || metric.includes("pressure")) &&
            metric !== heLevelMetric,
        );

        if (hePressureMetric) prioritizedMetrics.push(hePressureMetric);

        const otherMetrics = continuousMetrics
          .filter(
            (metric) => metric !== heLevelMetric && metric !== hePressureMetric,
          )
          .slice(0, Math.max(0, 5 - prioritizedMetrics.length));

        setSelectedMetrics([...prioritizedMetrics, ...otherMetrics]);
      }
    }
  }, [availableMetrics, selectedMetrics.length]);

  // Process chart data using custom hook
  const { chartData, yAxisConfig, dataStats, loading, error } = useChartData(
    rawData,
    selectedMetrics,
    dataConfig,
    timeRange,
  );

  // Apply unit conversion to chart data
  const {
    convertedChartData,
    availableUnitTypes,
    setUnitPreference,
    unitPreferences,
  } = useUnitConversion(chartData);

  // Get chart configuration using custom hook (use converted data)
  const { canvasConfig, fusionConfig, configControls } = useChartConfig(
    convertedChartData,
    yAxisConfig,
    darkMode,
  );

  // Handle metric selection changes
  const handleMetricSelectionChange = (newSelection) => {
    setSelectedMetrics(newSelection);
  };

  // Handle time range changes
  const handleTimeRangeChange = (newTimeRange) => {
    setTimeRange(newTimeRange);
  };

  // Handle fullscreen toggle
  const handleFullscreenToggle = () => {
    setIsFullscreen(!isFullscreen);
  };

  // Handle chart library toggle
  const handleChartLibraryChange = (library) => {
    setChartLibrary(library);
  };

  // Handle default field group selection
  const handleLoadDefaultGroup = () => {
    const defaultMetrics = loadDefaultFieldGroup();
    if (defaultMetrics.length > 0) {
      setSelectedMetrics(defaultMetrics);
    }
  };

  if (error) {
    return (
      <ErrorContainer>
        <Message type="error" header="Chart Error">
          {error}
        </Message>
      </ErrorContainer>
    );
  }

  return (
    <ChartContainer className={className} {...props}>
      {/* Data Point Selector Panel */}
      {showSelector && (
        <SelectorPanel>
          <DataPointSelector
            availableMetrics={availableMetrics}
            selectedMetrics={selectedMetrics}
            onSelectionChange={handleMetricSelectionChange}
            manufacturer={metadata?.manufacturer}
            rawData={rawData}
          />
        </SelectorPanel>
      )}

      {/* Main Chart Panel */}
      <ChartPanel expanded={!showSelector}>
        <ChartHeader>
          <HeaderLeft>
            <ChartTitle>
              System Data Visualization
              {metadata?.sme && <SystemName>({metadata.sme})</SystemName>}
            </ChartTitle>
            {dataStats && (
              <MetricsCount>
                {selectedMetrics.length} metric
                {selectedMetrics.length !== 1 ? "s" : ""} selected
              </MetricsCount>
            )}
          </HeaderLeft>

          <HeaderRight>
            <Button
              size="sm"
              appearance="ghost"
              startIcon={<ReloadIcon />}
              onClick={handleLoadDefaultGroup}
              title="Load default field group (Pressure, Comp Vib, Probes)"
            >
              Default Group
            </Button>
            <ChartControls
              timeRange={timeRange}
              onTimeRangeChange={handleTimeRangeChange}
              chartLibrary={chartLibrary}
              onChartLibraryChange={handleChartLibraryChange}
              configControls={configControls}
              onToggleSelector={() => setShowSelector(!showSelector)}
              showSelector={showSelector}
              availableUnitTypes={availableUnitTypes}
              onUnitChange={setUnitPreference}
            />
            <IconButton
              icon={<PlusIcon />}
              onClick={handleFullscreenToggle}
              appearance="subtle"
              size="sm"
              title="Open in fullscreen"
            />
          </HeaderRight>
        </ChartHeader>

        {/* Unit Toggle Controls */}
        {availableUnitTypes.length > 0 && (
          <>
            <UnitToggleRow>
              <UnitToggle
                availableUnitTypes={availableUnitTypes}
                setUnitPreference={setUnitPreference}
              />
            </UnitToggleRow>
            <Divider style={{ margin: "8px 0" }} />
          </>
        )}

        {availableUnitTypes.length === 0 && (
          <Divider style={{ margin: "12px 0" }} />
        )}

        {/* Chart Content */}
        <ChartContent>
          {selectedMetrics.length === 0 ? (
            <EmptyState>
              <EmptyStateIcon>📈</EmptyStateIcon>
              <EmptyStateTitle>No Data Points Selected</EmptyStateTitle>
              <EmptyStateDescription>
                Choose one or more metrics from the selector panel to display
                the chart.
              </EmptyStateDescription>
            </EmptyState>
          ) : loading ? (
            <LoadingState>
              <LoadingSpinner />
              <LoadingText>Processing chart data...</LoadingText>
            </LoadingState>
          ) : convertedChartData.length > 0 ? (
            <ChartRenderer
              chartData={convertedChartData}
              canvasConfig={canvasConfig}
              fusionConfig={fusionConfig}
              chartLibrary={chartLibrary}
              darkMode={darkMode}
            />
          ) : (
            <EmptyState>
              <EmptyStateIcon>📊</EmptyStateIcon>
              <EmptyStateTitle>No Data Available</EmptyStateTitle>
              <EmptyStateDescription>
                No data found for the selected metrics and time range.
              </EmptyStateDescription>
            </EmptyState>
          )}
        </ChartContent>

        {/* Data Statistics */}
        {dataStats && convertedChartData.length > 0 && (
          <>
            <Divider style={{ margin: "16px 0" }} />
            <StatsSection>
              <StatsTitle>Data Summary</StatsTitle>
              <StatsGrid>
                {Object.entries(dataStats).map(([metricKey, stats]) => {
                  const series = convertedChartData.find(
                    (s) => s.key === metricKey,
                  );
                  return (
                    <StatCard key={metricKey}>
                      <StatLabel>{series?.name || metricKey}</StatLabel>
                      <StatValues>
                        <StatValue>
                          <StatValueLabel>Latest:</StatValueLabel>
                          <StatValueNumber>
                            {stats.latest?.toFixed(2)} {series?.unit}
                          </StatValueNumber>
                        </StatValue>
                        <StatValue>
                          <StatValueLabel>Avg:</StatValueLabel>
                          <StatValueNumber>
                            {stats.avg.toFixed(2)} {series?.unit}
                          </StatValueNumber>
                        </StatValue>
                        <StatValue>
                          <StatValueLabel>Range:</StatValueLabel>
                          <StatValueNumber>
                            {stats.min.toFixed(1)} - {stats.max.toFixed(1)}{" "}
                            {series?.unit}
                          </StatValueNumber>
                        </StatValue>
                      </StatValues>
                    </StatCard>
                  );
                })}
              </StatsGrid>
            </StatsSection>
          </>
        )}
      </ChartPanel>

      {/* Fullscreen Modal */}
      <FullscreenModal
        open={isFullscreen}
        onClose={() => setIsFullscreen(false)}
        chartData={convertedChartData}
        canvasConfig={canvasConfig}
        fusionConfig={fusionConfig}
        chartLibrary={chartLibrary}
        configControls={configControls}
        onChartLibraryChange={handleChartLibraryChange}
        onTimeRangeChange={handleTimeRangeChange}
        timeRange={timeRange}
        metadata={metadata}
        darkMode={darkMode}
        availableUnitTypes={availableUnitTypes}
        onUnitChange={setUnitPreference}
      />
    </ChartContainer>
  );
};

// Styled Components
const ChartContainer = styled.div`
  display: flex;
  gap: 20px;
  height: 100%;
  min-height: 600px;
  padding: 8px;

  @media (max-width: 1200px) {
    flex-direction: column;
    gap: 16px;
  }
`;

const SelectorPanel = styled.div`
  flex: 0 0 350px;

  @media (max-width: 1200px) {
    flex: none;
  }
`;

const ChartPanel = styled(GlassCard)`
  flex: 1;
  padding: 20px;
  display: flex;
  flex-direction: column;
  min-height: 0;
  box-shadow: 0 0 16px rgba(0, 0, 0, 0.08);
  overflow: visible;

  ${({ expanded }) =>
    expanded &&
    `
    @media (max-width: 1200px) {
      min-height: 500px;
    }
  `}
`;

const ChartHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 16px;
  margin-bottom: 16px;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
  }
`;

const HeaderLeft = styled.div`
  flex: 1;
`;

const HeaderRight = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;

  @media (max-width: 768px) {
    justify-content: space-between;
  }
`;

const ChartTitle = styled.h2`
  margin: 0 0 4px 0;
  color: ${colors.neutral[800]};
  font-size: 20px;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const SystemName = styled.span`
  color: ${colors.neutral[600]};
  font-size: 16px;
  font-weight: normal;
`;

const MetricsCount = styled.div`
  color: ${colors.neutral[600]};
  font-size: 14px;
`;

const ChartContent = styled.div`
  flex: 1;
  min-height: 400px;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 60px 20px;
`;

const EmptyStateIcon = styled.div`
  font-size: 48px;
  margin-bottom: 16px;
`;

const EmptyStateTitle = styled.h3`
  margin: 0 0 8px 0;
  color: ${colors.neutral[700]};
  font-size: 18px;
  font-weight: 600;
`;

const EmptyStateDescription = styled.p`
  margin: 0;
  color: ${colors.neutral[600]};
  font-size: 14px;
  line-height: 1.5;
  max-width: 400px;
  margin: 0 auto;
`;

const LoadingState = styled.div`
  text-align: center;
  padding: 60px 20px;
`;

const LoadingSpinner = styled.div`
  width: 40px;
  height: 40px;
  border: 3px solid ${colors.neutral[200]};
  border-top: 3px solid ${colors.primary.main};
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin: 0 auto 16px;

  @keyframes spin {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(360deg);
    }
  }
`;

const LoadingText = styled.div`
  color: ${colors.neutral[600]};
  font-size: 14px;
`;

const ErrorContainer = styled.div`
  padding: 20px;
`;

const StatsSection = styled.div`
  margin-top: 16px;
`;

const StatsTitle = styled.h4`
  color: ${colors.neutral[700]};
  font-size: 16px;
  font-weight: 600;
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 12px;
`;

const StatCard = styled.div`
  background: ${colors.neutral[50]};
  border: 1px solid ${colors.neutral[200]};
  border-radius: 8px;
  padding: 12px;
`;

const StatLabel = styled.div`
  font-weight: 500;
  color: ${colors.neutral[800]};
  margin-bottom: 8px;
  font-size: 14px;
`;

const StatValues = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const StatValue = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const StatValueLabel = styled.span`
  color: ${colors.neutral[600]};
  font-size: 12px;
`;

const StatValueNumber = styled.span`
  color: ${colors.neutral[800]};
  font-size: 12px;
  font-weight: 500;
`;

const UnitToggleRow = styled.div`
  display: flex;
  align-items: center;
  padding: 8px 0;
  margin-top: 8px;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
  }
`;

export default UnifiedChart;
